import {
  makeApiProvider,
  useApiQuery,
  useSuspenseApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback, useMemo } from 'react';
import type {
  GetAllCountriesResult,
  GetLineItemConfigurationsResult,
  GetTaxCodesByCountryResult,
  ILineItemTaxPhoenixApi,
} from './api';
import { LineItemTaxPhoenixApi } from './api';
import type {
  CountriesTaxCodesControllerResultTypes,
  CountryResultDto,
  CreateLineItemConfigurationDto,
  LineItemConfigurationControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import type {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Phoenix-specific query keys to avoid cache collisions with legacy implementation
export const GET_TAX_LINE_CONFIGURATIONS_PHOENIX = (
  globalServiceProviderId: string | undefined
): QueryKey => ['get-tax-line-configurations-phoenix', globalServiceProviderId];

export const GET_TAX_CODES_BY_COUNTRY_PHOENIX = (
  countryId: string | undefined
): QueryKey => [
  'get-tax-codes-by-country-phoenix',
  ...(countryId ? [countryId] : []),
];

export const GET_ALL_COUNTRIES_PHOENIX = (): QueryKey => [
  'get-all-countries-phoenix',
];

export const CREATE_TAX_LINE_CONFIGURATION_PHOENIX = (
  globalServiceProviderId: string | undefined
): QueryKey => [
  'create-tax-line-configurations-phoenix',
  globalServiceProviderId,
];

export const DELETE_TAX_LINE_CONFIGURATION_PHOENIX = (
  globalServiceProviderId: string | undefined
): QueryKey => [
  'delete-tax-line-configurations-phoenix',
  globalServiceProviderId,
];

const ApiContext = createContext<ILineItemTaxPhoenixApi | undefined>(undefined);
const {
  useApi: useLineItemTaxPhoenixApi,
  ApiProvider: LineItemTaxPhoenixApiProvider,
} = makeApiProvider({
  name: 'LineItemTaxPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new LineItemTaxPhoenixApi(tenantId, auth, apiUrl),
});

export {
  useLineItemTaxPhoenixApi,
  LineItemTaxPhoenixApiProvider,
  ApiContext as LineItemTaxPhoenixApiContext,
};
export type { ILineItemTaxPhoenixApi } from './api';

export interface CountryWithTranslatedLabel extends CountryResultDto {
  label: string;
  value: string;
}

export const useGetTaxCodesByCountryPhoenix = (
  countryId: string | undefined
): UseQueryResult<
  CountriesTaxCodesControllerResultTypes['getTaxCodes'],
  unknown
> => {
  const select = useCallback(
    (
      data: GetTaxCodesByCountryResult
    ): CountriesTaxCodesControllerResultTypes['getTaxCodes'] => {
      return data.data.map((row) => ({
        id: row.id,
        countryId: row.country.id,
        countryIsoCode: row.country.iso_code,
        name: row.tax_name,
        countryIsoCode3: row.country.iso_code_3,
        countryName: row.country.name,
        taxRatio: parseFloat(row.tax_amount) * 100,
        abbreviation: row.tax_abbreviation,
        displayTaxRatioExplanation: row.display_explanation,
        taxRatioExplanation: row.explanation_long,
        paragraph: row.paragraph,
      }));
    },
    []
  );

  return useApiQuery(
    useLineItemTaxPhoenixApi,
    GET_TAX_CODES_BY_COUNTRY_PHOENIX(countryId),
    (api) => {
      if (!countryId) {
        throw new Error('countryId is required');
      }

      return api.getTaxCodesByCountry({
        params: {
          countryId,
        },
      });
    },
    { enabled: !!countryId, select }
  );
};

export const useGetTaxCodesByCountryPhoenixSuspense = (
  countryId: string | undefined,
  options?: Omit<
    UseQueryOptions<
      GetTaxCodesByCountryResult,
      unknown,
      CountriesTaxCodesControllerResultTypes['getTaxCodes']
    >,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<
  CountriesTaxCodesControllerResultTypes['getTaxCodes'],
  unknown
> => {
  const select = useCallback(
    (
      data: GetTaxCodesByCountryResult
    ): CountriesTaxCodesControllerResultTypes['getTaxCodes'] => {
      return data.data.map((row) => ({
        id: row.id,
        countryId: row.country.id,
        countryIsoCode: row.country.iso_code,
        name: row.tax_name,
        countryIsoCode3: row.country.iso_code_3,
        countryName: row.country.name,
        taxRatio: parseFloat(row.tax_amount) * 100,
        abbreviation: row.tax_abbreviation,
        displayTaxRatioExplanation: row.display_explanation,
        taxRatioExplanation: row.explanation_long,
        paragraph: row.paragraph,
      }));
    },
    []
  );

  return useSuspenseApiQuery(
    useLineItemTaxPhoenixApi,
    GET_TAX_CODES_BY_COUNTRY_PHOENIX(countryId),
    (api) => {
      if (!countryId) {
        throw new Error('countryId is required');
      }

      return api.getTaxCodesByCountry({
        params: {
          countryId,
        },
      });
    },
    {
      ...options,
      select,
    }
  );
};

export const useGetAllCountriesPhoenix = (
  options?: Omit<
    UseQueryOptions<
      GetAllCountriesResult,
      unknown,
      CountryWithTranslatedLabel[]
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<CountryWithTranslatedLabel[], unknown> => {
  const { t } = useTranslation();

  const translateAndSortCountries = useCallback(
    (countries: GetAllCountriesResult): CountryWithTranslatedLabel[] => {
      const options = countries.data?.map<CountryWithTranslatedLabel>(
        (country) => {
          return {
            id: country.id,
            isoCode: country.iso_code,
            name: country.name,
            isoCode3: country.iso_code_3,
            numericCode: country.numeric_code,
            value: country.id,
            label: t(`country.${country.iso_code}`),
          };
        }
      );

      options?.sort((a, b) => a.label.localeCompare(b.label));
      return options ?? [];
    },
    [t]
  );

  return useApiQuery<
    LineItemTaxPhoenixApi,
    CountryWithTranslatedLabel[],
    GetAllCountriesResult
  >(
    useLineItemTaxPhoenixApi,
    GET_ALL_COUNTRIES_PHOENIX(),
    (api) => {
      return api.getAllCountries();
    },
    {
      ...options,
      select: translateAndSortCountries,
    }
  );
};

export const useGetAllCountriesPhoenixSuspense = (
  options?: Omit<
    UseQueryOptions<
      GetAllCountriesResult,
      unknown,
      CountryWithTranslatedLabel[]
    >,
    'queryFn' | 'queryKey'
  >
): UseSuspenseQueryResult<CountryWithTranslatedLabel[], unknown> => {
  const { t } = useTranslation();

  const translateAndSortCountries = useCallback(
    (countries: GetAllCountriesResult): CountryWithTranslatedLabel[] => {
      const options = countries.data?.map<CountryWithTranslatedLabel>(
        (country) => {
          return {
            id: country.id,
            isoCode: country.iso_code,
            name: country.name,
            isoCode3: country.iso_code_3,
            numericCode: country.numeric_code,
            value: country.id,
            label: t(`country.${country.iso_code}`),
          };
        }
      );

      options?.sort((a, b) => a.label.localeCompare(b.label));
      return options ?? [];
    },
    [t]
  );

  return useSuspenseApiQuery<
    LineItemTaxPhoenixApi,
    CountryWithTranslatedLabel[],
    GetAllCountriesResult
  >(
    useLineItemTaxPhoenixApi,
    GET_ALL_COUNTRIES_PHOENIX(),
    (api) => {
      return api.getAllCountries();
    },
    {
      ...options,
      select: translateAndSortCountries,
    }
  );
};

export const useSortedCountries = (
  countries: CountryWithTranslatedLabel[]
): CountryWithTranslatedLabel[] => {
  return useMemo(() => {
    if (!countries) {
      return [];
    }

    // Filter out any undefined or null elements
    const validCountries = countries.filter(
      (country) => country !== null && country !== undefined
    );

    const priorityCountryCodes = ['AT', 'DE', 'FR'];
    const priorityCountries = validCountries
      .filter((country) => priorityCountryCodes.includes(country.isoCode))
      .map(
        (country) =>
          ({
            label: country.label,
            value: country.id,
            id: country.id,
            isoCode: country.isoCode,
            isoCode3: country.isoCode3,
            numericCode: country.numericCode,
            name: country.name,
          } as CountryWithTranslatedLabel)
      )
      // Sort by priority: Austria, Germany, France
      .sort((a, b) => {
        const indexA = priorityCountryCodes.indexOf(a.isoCode);
        const indexB = priorityCountryCodes.indexOf(b.isoCode);
        return indexA - indexB;
      });

    const otherCountries = validCountries
      .filter((country) => !priorityCountryCodes.includes(country.isoCode))
      .map(
        (country) =>
          ({
            label: country.label,
            value: country.id,
            id: country.id,
            isoCode: country.isoCode,
            isoCode3: country.isoCode3,
            numericCode: country.numericCode,
            name: country.name,
          } as CountryWithTranslatedLabel)
      );

    return [...priorityCountries, ...otherCountries];
  }, [countries]);
};

export const useGetTaxLineConfigurationsPhoenix = <
  TData = LineItemConfigurationControllerResultTypes['getLineItemConfigurations']
>(
  globalServiceProviderId: string | undefined,
  options?: {
    enabled?: boolean;
    select?: (
      data: LineItemConfigurationControllerResultTypes['getLineItemConfigurations']
    ) => TData;
  }
): UseQueryResult<TData, unknown> => {
  type TransformedData =
    LineItemConfigurationControllerResultTypes['getLineItemConfigurations'];

  const internalSelect = useCallback(
    (data: GetLineItemConfigurationsResult): TransformedData => {
      return data.data.map((row) => ({
        id: row.id,
        name: row.name,
        globalServiceProviderId: row.global_service_provider_id,
        amount: row.amount,
        taxCodes: row.taxes.map((tax) => ({
          id: tax.id,
          countryId: tax.country_id,
          displayExplanation: tax.display_explanation,
          explanationLong: tax.explanation_long,
          explanationShort: tax.explanation_short,
          taxAbbreviation: tax.tax_abbreviation,
          taxAmount: parseFloat(tax.tax_amount),
          taxName: tax.tax_name,
        })),
      }));
    },
    []
  );

  // Compose selects: apply internal transformation first, then user's select if provided
  const composedSelect = useCallback(
    (rawData: GetLineItemConfigurationsResult): TData => {
      const transformedData = internalSelect(rawData);
      if (options?.select) {
        return options.select(transformedData);
      }
      return transformedData as TData;
    },
    // Only track options.select to avoid unnecessary re-renders when other options properties change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalSelect, options?.select]
  );

  return useApiQuery(
    useLineItemTaxPhoenixApi,
    GET_TAX_LINE_CONFIGURATIONS_PHOENIX(globalServiceProviderId),
    (api) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      return api.getLineItemConfigurations();
    },
    {
      enabled: !!globalServiceProviderId && (options?.enabled ?? true),
      select: composedSelect,
    }
  );
};

export const useCreateLineItemConfigurationPhoenix = (
  globalServiceProviderId: string | undefined
): UseMutationResult<void, unknown, CreateLineItemConfigurationDto> => {
  const api = useLineItemTaxPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, CreateLineItemConfigurationDto>({
    mutationKey: CREATE_TAX_LINE_CONFIGURATION_PHOENIX(globalServiceProviderId),
    mutationFn: async (data: CreateLineItemConfigurationDto) => {
      if (!globalServiceProviderId) {
        throw new Error('globalServiceProviderId is required');
      }
      await api.createLineItemConfiguration({
        body: data,
      });
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: GET_TAX_LINE_CONFIGURATIONS_PHOENIX(globalServiceProviderId),
      });
    },
  });
};

export const useDeleteLineItemConfigurationPhoenix = (
  globalServiceProviderId: string | undefined
): UseMutationResult<
  { status: 'OK' },
  unknown,
  {
    id: string;
  }
> => {
  const api = useLineItemTaxPhoenixApi();
  const queryClient = useQueryClient();
  return useMutation<
    { status: 'OK' },
    unknown,
    {
      id: string;
    }
  >({
    mutationKey: DELETE_TAX_LINE_CONFIGURATION_PHOENIX(globalServiceProviderId),
    mutationFn: (data: { id: string }) => {
      return api.deleteLineItemConfiguration({
        params: {
          id: data.id,
        },
      });
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: GET_TAX_LINE_CONFIGURATIONS_PHOENIX(globalServiceProviderId),
      });
    },
  });
};
